import React, { useContext, useState, useEffect } from "react";
import {
  useTheme,
  Grid,
  Box,
  Typography,
  Button,
  FormGroup,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { CCMPatient } from "../../interfaces/CCMPatient";
import PatientService from "../../services/PatientService";
import useFetch from "../../hooks/useFetch";
import { UserContext } from "../../context/UserContext";
import { format } from "date-fns";
import PatientPlanInfo from "./PatientPlanInfo";
import PatientDialog from "./PatientDialog";

export const PatientManagement: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [cookies] = useCookies(["access_token"]);
  const [search, setSearch] = useState("");
  const [tableHeight, setTableHeight] = useState("500px");
  const token = cookies.access_token;
  const [openPatientPlan, setOpenPatientPlan] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [newPatientId, setNewPatientId] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmailId, setNewEmailId] = useState("");
  // New state for Date of Birth and Gender.
  const [newDOB, setNewDOB] = useState("");
  const [newGender, setNewGender] = useState("M");
  const [addPatientError, setAddPatientError] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<CCMPatient | null>(null);
  const [disabledPatients, setDisabledPatients] = useState(false);
  const context = useContext(UserContext);
  const [user] = useState(context?.user);
  // We'll use this state to hold the current patient list.
  const [sortedPatientList, setSortedPatientList] = useState<CCMPatient[]>([]);
  const my_organization_id = user?.organization || "";

  // New state for timezone selection. Defaults to the browser's timezone.
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  useEffect(() => {
    const updateHeight = () => {
      const maxHeight = window.innerHeight - 250;
      setTableHeight(`${maxHeight}px`);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const { data: patientlist, refresh } = useFetch(
    my_organization_id
      ? {
          url: PatientService.getPatients(my_organization_id, 0, disabledPatients),
          access_token: token,
        }
      : { url: "", access_token: "" }
  );

  useEffect(() => {
    if (!patientlist) {
      return;
    }
    console.log("Raw patient data from PatientService.getPatients (PatientManagement):", patientlist);
    console.log("Patient count:", patientlist.length);
    if (patientlist.length > 0) {
      console.log("Sample patient object (first patient):", patientlist[0]);
      console.log("Patient object keys:", Object.keys(patientlist[0]));
    }
    const sortedList = patientlist.sort((a: CCMPatient, b: CCMPatient) => {
      const nameA = a.patient_id.toLowerCase();
      const nameB = b.patient_id.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    setSortedPatientList(sortedList);
  }, [patientlist]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledPatients]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const filteredPatients = sortedPatientList.filter((patient: CCMPatient) =>
    patient.patient_id.toLowerCase().includes(search.toLowerCase()) ||
    (patient.patient_groups &&
      patient.patient_groups.join(", ").toLowerCase().includes(search.toLowerCase())) ||
    (patient.last_name && patient.last_name.toLowerCase().includes(search.toLowerCase())) ||
    (patient.first_name && patient.first_name.toLowerCase().includes(search.toLowerCase()))
  );

  const handleOpenRegisterDialog = () => {
    setOpenRegisterDialog(true);
  };

  const handleCloseRegisterDialog = () => {
    setOpenRegisterDialog(false);
    setNewPatientId("");
    setAddPatientError("");
  };

  const handleOpenPatientPlan = (patient: CCMPatient) => {
    setSelectedPatient(patient);
    setSelectedPatientId(patient.patient_id);
    setOpenPatientPlan(true);
  };

  const handleClosePatientPlan = () => {
    setSelectedPatient(null);
    setOpenPatientPlan(false);
    setSelectedPatientId(null);
  };

  const handlePlanAssigned = async (patient_id: string) => {
    try {
      const updatedPlan = await PatientService.fetchEngagementPlan(token, patient_id);
      if (!updatedPlan) return;
      const patientgroups: string[] = [];
      for (let item of updatedPlan) {
        patientgroups.push(item.patient_group);
      }
      setSortedPatientList((prevPatients: CCMPatient[]) =>
        prevPatients.map((patient: CCMPatient) =>
          patient.patient_id === patient_id
            ? { ...patient, patient_groups: patientgroups }
            : patient
        )
      );
    } catch (error) {
      console.error("Failed to update patient engagement plan:", error);
    }
  };

  const handleRegisterPatient = async () => {
    if (!newPatientId) return;
    try {
      const data = {
        organization_id: my_organization_id,
        "elation-id": newPatientId,
        timezone: selectedTimezone,
        patient_group: "",
        engagementhistory: [],
        last_name: newLastName,
        first_name: newFirstName,
        email: newEmailId,
        phone: newPhone,
        status: "",
        last_contact: "",
        disabled: false,
        dob: newDOB,
        gender: newGender,
      };
      await PatientService.registerPatient(cookies.access_token, data).then((response) => {
        if (response.error) {
          setAddPatientError(response.error);
          throw new Error(`Error adding patient: ${response.error}`);
        }
      });

      // Create a new patient object locally.
      const newPatient: CCMPatient = {
        patient_id: newPatientId,
        patient_user_id: "", // Assuming user_id is not available at this point.
        patient_groups: [],
        engagementhistory: [],
        last_name: newLastName,
        first_name: newFirstName,
        timezone: selectedTimezone,
        email: newEmailId,
        phone: newPhone,
        status: "",
        last_contact: "",
        active: true,
        measurement_days: 0,
        call_time: 0,
        dob: newDOB,
        gender: newGender,
      };

      // Manually update the patient list without an API call.
      const updatedList = [...sortedPatientList, newPatient].sort((a: CCMPatient, b: CCMPatient) =>
        a.patient_id.toLowerCase().localeCompare(b.patient_id.toLowerCase())
      );
      setSortedPatientList(updatedList);

      // Clear inputs and close the dialog.
      setNewPatientId("");
      setNewFirstName("");
      setNewLastName("");
      setNewPhone("");
      setNewEmailId("");
      setNewDOB("");
      setNewGender("M");
      setOpenRegisterDialog(false);
    } catch (error) {
      console.error("Failed to register new patient:", error);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case "newPatientId":
        setNewPatientId(value);
        break;
      case "firstName":
        setNewFirstName(value);
        break;
      case "lastName":
        setNewLastName(value);
        break;
      case "phone":
        setNewPhone(value);
        break;
      case "emailId":
        setNewEmailId(value);
        break;
      case "dob":
        setNewDOB(value);
        break;
      case "gender":
        setNewGender(value);
        break;
      default:
        break;
    }
  };

  const handleTogglePatients = () => {
    setDisabledPatients(!disabledPatients);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: theme.spacing(2) }}>
      {/* Search Bar */}
      <Box sx={{ mb: theme.spacing(2) }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={10}>
            <TextField
              label={t("Search Patients")}
              variant="outlined"
              fullWidth
              value={search}
              onChange={handleSearchChange}
            />
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="primary" onClick={handleOpenRegisterDialog}>
              {t("Enroll Patient")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={4}>
        {/* Patient List Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: theme.spacing(2), overflow: "hidden" }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t("Patient List")}</Typography>
              <FormGroup row sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ mr: 1 }}>{t("Active")}</Typography>
                <Switch
                  checked={disabledPatients}
                  onChange={handleTogglePatients}
                  color="primary"
                  sx={{ verticalAlign: "middle" }}
                />
                <Typography sx={{ ml: 1 }}>{t("Inactive")}</Typography>
              </FormGroup>
            </Grid>

            <TableContainer sx={{ maxHeight: tableHeight, overflowY: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("ID")}</TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("Name")}</TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("CCM Plan")}</TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("Last Contact")}</TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPatients.map((patient: CCMPatient) => (
                    <TableRow key={patient.patient_id} sx={{ height: "36px" }}>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        <Typography
                          sx={{ cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => handleOpenPatientPlan(patient)}
                        >
                          {patient.patient_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {(patient?.last_name && patient?.first_name)
                          ? `${patient.last_name}, ${patient.first_name}`
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.patient_groups.join(", ") || "-"}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.patient_groups && patient.last_contact
                          ? format(new Date(patient.last_contact), "MMM dd, yyyy HH:mm")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {selectedPatientId && selectedPatient && (
        <PatientPlanInfo
          patient={selectedPatient}
          patient_id={selectedPatientId}
          token={token}
          open={openPatientPlan}
          setOpen={setOpenPatientPlan}
          onClose={handleClosePatientPlan}
          updatePatientList={handlePlanAssigned}
          onStatusChange={refresh}
        />
      )}

      <PatientDialog
        open={openRegisterDialog}
        newPatientId={newPatientId}
        addPatientError={addPatientError}
        timezone={selectedTimezone}
        onTimezoneChange={setSelectedTimezone}
        dob={newDOB}
        gender={newGender}
        onClose={handleCloseRegisterDialog}
        onRegister={handleRegisterPatient}
        onInputChange={handleInputChange}
      />
    </Box>
  );
};
